import { AdditiveBlending, BufferAttribute, BufferGeometry, Color, Group, LineSegments, Points, ShaderMaterial, Vector3 } from 'three';
import { SeededRandom } from '../core/SeededRandom';
import type { ResolvedQuality } from '../types';

/** Cosmetic dust repeats outside a faded boundary; destinations never wrap. */
export function wrappedRelative(point: Vector3, eye: Vector3, span: number) {
  const wrap = (n: number) => ((n + span / 2) % span + span) % span - span / 2;
  return new Vector3(wrap(point.x - eye.x), wrap(point.y - eye.y), wrap(point.z - eye.z));
}

const vertex = /* glsl */`
attribute vec3 aColor;
attribute float aSize;
attribute float aTail;
uniform vec3 uOrigin;
uniform vec3 uVelocity;
uniform float uSpan;
uniform float uRatio;
uniform float uOpacity;
uniform float uExposure;
varying vec3 vColor;
varying float vAlpha;
void main() {
  vec3 relative = mod(position - uOrigin + uSpan * .5, uSpan) - uSpan * .5;
  float distanceFromEye = length(relative);
  float fade = (1. - smoothstep(uSpan * .34, uSpan * .48, distanceFromEye)) * smoothstep(3., 12., distanceFromEye);
  // The tail is the earlier relative position of a stationary world particle.
  relative += uVelocity * uExposure * aTail;
  vec3 viewPosition = mat3(viewMatrix) * relative;
  gl_Position = projectionMatrix * vec4(viewPosition, 1.);
  gl_PointSize = clamp(aSize * uRatio * uSpan * .35 / max(12., -viewPosition.z), .7 * uRatio, 3.8 * uRatio);
  vColor = aColor;
  vAlpha = fade * uOpacity * (1. - aTail * .85);
}
`;
const pointFragment = /* glsl */`
varying vec3 vColor; varying float vAlpha;
void main() {
  float d = length(gl_PointCoord - .5);
  if (d > .5) discard;
  gl_FragColor = vec4(vColor, vAlpha * exp(-d * d * 22.));
}
`;
const trailFragment = /* glsl */`
varying vec3 vColor; varying float vAlpha;
void main() { gl_FragColor = vec4(vColor, vAlpha); }
`;

interface DustLayer { points: Points<BufferGeometry, ShaderMaterial>; span: number; count: number }

export class SpaceDust {
  readonly root = new Group();
  private layers: DustLayer[] = [];
  private trails: LineSegments<BufferGeometry, ShaderMaterial>;
  private velocity = new Vector3();

  constructor() {
    this.root.name = 'World-anchored depth layers';
    for (const [count, span, seed, opacity] of [[1200, 320, 811, .65], [1800, 2400, 913, .6], [2600, 16000, 177, .5]]) {
      const random = new SeededRandom(seed), positions = new Float32Array(count * 3), colors = new Float32Array(count * 3), sizes = new Float32Array(count);
      const cool = new Color('#a0d6e4'), warm = new Color('#dec8a5');
      for (let i = 0; i < count; i++) {
        positions.set([random.signed(span / 2), random.signed(span / 2), random.signed(span / 2)], i * 3);
        cool.clone().lerp(warm, random.next()).multiplyScalar(random.range(.6, 1.2)).toArray(colors, i * 3);
        sizes[i] = random.range(.9, 2.3);
      }
      const geometry = new BufferGeometry();
      geometry.setAttribute('position', new BufferAttribute(positions, 3));
      geometry.setAttribute('aColor', new BufferAttribute(colors, 3));
      geometry.setAttribute('aSize', new BufferAttribute(sizes, 1));
      geometry.setAttribute('aTail', new BufferAttribute(new Float32Array(count), 1));
      const material = this.material(span, opacity, pointFragment);
      const points = new Points(geometry, material); points.frustumCulled = false;
      this.root.add(points); this.layers.push({ points, span, count });
    }
    const first = this.layers[0], geometry = new BufferGeometry();
    for (const name of ['position', 'aColor', 'aSize']) {
      const source = first.points.geometry.getAttribute(name), data = new Float32Array(first.count * 2 * source.itemSize);
      for (let i = 0; i < first.count; i++) for (let end = 0; end < 2; end++) for (let axis = 0; axis < source.itemSize; axis++) data[(i * 2 + end) * source.itemSize + axis] = source.array[i * source.itemSize + axis];
      geometry.setAttribute(name, new BufferAttribute(data, source.itemSize));
    }
    geometry.setAttribute('aTail', new BufferAttribute(Float32Array.from({ length: first.count * 2 }, (_, i) => i % 2), 1));
    this.trails = new LineSegments(geometry, this.material(first.span, 0, trailFragment));
    this.trails.frustumCulled = false; this.trails.visible = false; this.root.add(this.trails);
  }

  private material(span: number, opacity: number, fragmentShader: string) {
    return new ShaderMaterial({ vertexShader: vertex, fragmentShader, transparent: true, blending: AdditiveBlending, depthWrite: false,
      uniforms: { uOrigin: { value: new Vector3() }, uVelocity: { value: new Vector3() }, uSpan: { value: span }, uRatio: { value: 1 }, uOpacity: { value: opacity }, uExposure: { value: 0 } },
    });
  }
  setQuality(quality: ResolvedQuality, ratio: number) {
    const amount = quality === 'high' ? 1 : quality === 'medium' ? .7 : .4;
    for (const layer of this.layers) { layer.points.geometry.setDrawRange(0, Math.floor(layer.count * amount)); layer.points.material.uniforms.uRatio.value = ratio; }
    this.trails.geometry.setDrawRange(0, Math.floor(this.layers[0].count * amount) * 2);
  }
  update(position: Vector3, velocity: Vector3, reduced: boolean) {
    this.velocity.copy(velocity);
    for (const { points, span } of this.layers) {
      // Keep uniform magnitudes small even on a long, uninterrupted journey.
      points.material.uniforms.uOrigin.value.set(position.x % span, position.y % span, position.z % span);
    }
    const uniforms = this.trails.material.uniforms;
    uniforms.uOrigin.value.copy(this.layers[0].points.material.uniforms.uOrigin.value);
    uniforms.uVelocity.value.copy(velocity);
    uniforms.uExposure.value = Math.min(.022, 18 / Math.max(1, velocity.length()));
    uniforms.uOpacity.value = reduced ? 0 : Math.min(.58, Math.max(0, (velocity.length() - 24) / 300));
    this.trails.visible = !reduced && velocity.length() > 24;
  }
  sample(eye: Vector3, layerIndex = 0, index = 0) {
    const layer = this.layers[layerIndex];
    return wrappedRelative(new Vector3().fromBufferAttribute(layer.points.geometry.getAttribute('position'), index), eye, layer.span);
  }
  get stats() { return { depthParticles: this.layers.map(layer => layer.points.geometry.drawRange.count), motionTrails: this.trails.visible, trailVelocity: this.velocity.toArray().map(n => Number(n.toFixed(2))) }; }
}
